"use client";

import { ChevronRight } from "lucide-react";
import { Button } from "../../../elements/button/button";
import { Image } from "../../../elements/image/image";
import { TextD2C } from "../../../elements/text-d2c/text";
import { mainNavigationClasses } from "../main-navigation.constants";

export interface NavigationCartItem {
  id: string;
  name: string;
  href?: string;
  image?: { src: string; alt: string };
  /** Preformatted variant details supplied by the storefront. */
  variantDescription?: string;
  /** Variant details such as colour and size, shown as "Label: value". */
  attributes?: Array<{ label: string; value: string }>;
  quantity: number;
  /** Formatted prices, e.g. "€6,999". */
  unitPrice: string;
  totalPrice: string;
}

export interface NavigationCartProps {
  /** E.g. "The product has been added to the cart." */
  title: string;
  items: NavigationCartItem[];
  viewCartHref: string;
  checkoutHref: string;
  onContinueShopping: () => void;
  microcopies: {
    quantity: string;
    unitPrice: string;
    total: string;
    viewCart: string;
    continueShopping: string;
    checkout: string;
  };
}

const buttonClassName = "h-10.5 w-full";
const priceRowClassName = "flex justify-between gap-1 sm:justify-end";

/**
 * Cart panel of the header, e.g. to confirm a product added to the cart. It
 * lists the entries it is given; loading them is up to the app.
 */
export function NavigationCart(props: Readonly<NavigationCartProps>) {
  return (
    <div className="flex h-full min-h-0 flex-col gap-3 lg:h-auto lg:max-h-[calc(100dvh-9.5rem)]">
      <div className="shrink-0">
        <TextD2C style="no-mb" tagName="h2" variant="h5">
          {props.title}
        </TextD2C>
      </div>

      <ul className="flex min-h-0 flex-1 flex-col divide-y divide-alabastergrey overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {props.items.map((item) => (
          <li className="flex items-center gap-4 py-3 lg:gap-6" key={item.id}>
            <div className="w-2/5 max-w-[150px] shrink-0">
              {item.image ? (
                <Image
                  alt={item.image.alt}
                  aspectRatio="1:1"
                  fill={true}
                  loading="lazy"
                  objectFit="contain"
                  sizes="150px"
                  src={item.image.src}
                />
              ) : null}
            </div>
            {/* On phones the prices continue the details as justified lines,
                on wider screens they form a column on the right. */}
            <div className="flex min-w-0 flex-1 flex-col font-brand text-[12px] text-black leading-[17px] sm:flex-row sm:items-center sm:justify-between sm:gap-3">
              <div className="flex min-w-0 flex-col">
                {item.href ? (
                  <Button
                    // The button's own size would set the name in 16px.
                    className={`${mainNavigationClasses.focusRing} justify-start whitespace-normal text-left font-bold text-[12px] leading-[17px] tracking-[0.02em] hover:underline`}
                    href={item.href}
                    mode="D2C"
                    unstyled={true}
                  >
                    {item.name}
                  </Button>
                ) : (
                  <p className="font-bold tracking-[0.02em]">{item.name}</p>
                )}
                {item.variantDescription ? (
                  <p>{item.variantDescription}</p>
                ) : null}
                {item.attributes?.map((attribute) => (
                  <p key={attribute.label}>
                    {attribute.label}: {attribute.value}
                  </p>
                ))}
              </div>
              <dl className="flex shrink-0 flex-col sm:items-end">
                <div className={priceRowClassName}>
                  <dt>{props.microcopies.quantity}:</dt>
                  <dd>{item.quantity}</dd>
                </div>
                {item.unitPrice ? (
                  <div className={priceRowClassName}>
                    <dt>{props.microcopies.unitPrice}:</dt>
                    <dd className="font-bold">{item.unitPrice}</dd>
                  </div>
                ) : null}
                <div className={priceRowClassName}>
                  <dt>{props.microcopies.total}:</dt>
                  <dd className="font-bold">{item.totalPrice}</dd>
                </div>
              </dl>
            </div>
          </li>
        ))}
      </ul>

      {/* Checkout takes a full, taller line on phones and half the line next
          to the other two buttons on wider screens. */}
      <div className="grid shrink-0 grid-cols-2 gap-2 pt-2 sm:grid-cols-4">
        <Button
          className={buttonClassName}
          href={props.viewCartHref}
          mode="D2C"
          size="sm"
          variant="outline"
        >
          {props.microcopies.viewCart}
        </Button>
        <Button
          className={buttonClassName}
          mode="D2C"
          onClick={props.onContinueShopping}
          size="sm"
          type="button"
          variant="outline"
        >
          {props.microcopies.continueShopping}
        </Button>
        <Button
          className={`${buttonClassName} col-span-2 max-sm:h-13`}
          href={props.checkoutHref}
          mode="D2C"
          size="sm"
        >
          {props.microcopies.checkout}
          <ChevronRight aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}
